import { MutableRefObject, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { CMEDisabledCapabilitiesConfigDTO, getCMEDisabledCapabilitiesConfig } from '../../Common/API/GenericConfigAPI'
import { ReactState } from '../../Common/Contexts'

/**
 * CX Suite products shown as columns on the CME Capabilities tab. Values are the capability keys
 * used in the `cme-disabledCapabilities-config` generic config.
 */
export enum CapabilityProduct {
    CXA = 'CXA',
    CXI = 'CXI',
    CXS = 'CXS',
    CXR = 'CXR',
    CXT = 'CXT'
}

/**
 * TelSys types that have CX Suite capability controls. Values are the TelSys type keys used in the
 * `cme-disabledCapabilities-config` generic config.
 */
export enum CapabilityPlatform {
    BROADWORKS = 'BroadWorks',
    WEBEX = 'Webex',
    TEAMS = 'MSTeams'
}

/**
 * The products that let CX Transcribe be turned on for a TelSys type: any one of them must be ticked for the
 * same type. BroadWorks has no CX Record-only scenario, so only CX Score qualifies there.
 */
export const CXT_QUALIFYING_PRODUCTS: Readonly<Record<CapabilityPlatform, readonly CapabilityProduct[]>> = {
    [CapabilityPlatform.BROADWORKS]: [CapabilityProduct.CXS],
    [CapabilityPlatform.WEBEX]: [CapabilityProduct.CXS, CapabilityProduct.CXR],
    [CapabilityPlatform.TEAMS]: [CapabilityProduct.CXS, CapabilityProduct.CXR]
}

/**
 * Whether CX Transcribe can be on for a TelSys type, given which products are ticked for that type.
 *
 * @param platform The TelSys type.
 * @param tickedProducts The products ticked for that TelSys type.
 * @returns True when at least one qualifying product is ticked.
 */
export const isCxtQualified = (platform: CapabilityPlatform, tickedProducts: ReadonlySet<CapabilityProduct>): boolean =>
    CXT_QUALIFYING_PRODUCTS[platform].some((product: CapabilityProduct): boolean => tickedProducts.has(product))

/**
 * Whether CX Transcribe can be on for a TelSys type, given whether CX Score and CX Record are on for that type.
 *
 * @param platform The TelSys type.
 * @param hasCxScore Whether CX Score is on for the TelSys type.
 * @param hasCxRecord Whether CX Record is on for the TelSys type.
 * @returns True when at least one qualifying product is on.
 */
export const isCxtQualifiedBy = (platform: CapabilityPlatform, hasCxScore: boolean, hasCxRecord: boolean): boolean =>
    isCxtQualified(
        platform,
        new Set<CapabilityProduct>([
            ...(hasCxScore ? [CapabilityProduct.CXS] : []),
            ...(hasCxRecord ? [CapabilityProduct.CXR] : [])
        ])
    )

/**
 * The `disabledCapabilities` section of the generic config: unavailable capability keys under `TelSysType`,
 * keyed by TelSys type key, and the TelSys-independent flags under `Common`.
 */
export type DisabledCapabilities = Readonly<{
    TelSysType?: Readonly<Partial<Record<string, readonly string[]>>>
    Common?: Readonly<{ aiAssist?: boolean }>
}>

/**
 * Whether a product/TelSys type combination is available (not marked unavailable in the generic config).
 *
 * Mirrors the BE rules: a missing config, or no entry for the TelSys type, means everything is available,
 * and unknown TelSys types, capability keys or malformed entries are ignored.
 *
 * @param disabledCapabilities The unavailable capabilities from the generic config, or null when there is none.
 * @param platform The TelSys type of the combination.
 * @param product The product of the combination.
 * @returns True when the combination can be offered.
 */
export const isCapabilityAvailable = (
    disabledCapabilities: Nullable<DisabledCapabilities>,
    platform: CapabilityPlatform,
    product: CapabilityProduct
): boolean => {
    const disabledForPlatform: unknown = disabledCapabilities?.TelSysType?.[platform]

    // A malformed entry (not a list) is ignored rather than crashing the tab or substring-matching a string.
    return !(Array.isArray(disabledForPlatform) && disabledForPlatform.includes(product))
}

/**
 * Whether AI Assist is available (not marked unavailable by `Common.aiAssist` in the generic config).
 * A missing config or a missing `Common.aiAssist` key means it is available, as on the BE.
 *
 * @param disabledCapabilities The unavailable capabilities from the generic config, or null when there is none.
 * @returns True when AI Assist can be offered.
 */
export const isAiAssistAvailable = (disabledCapabilities: Nullable<DisabledCapabilities>): boolean =>
    disabledCapabilities?.Common?.aiAssist !== true

/**
 * Loads the unavailable CME capabilities from the `cme-disabledCapabilities-config` generic config.
 *
 * @returns The unavailable capabilities, or null while loading or when the config is missing or unreadable
 *          (everything available).
 */
export const useDisabledCapabilities = (): Nullable<DisabledCapabilities> => {
    const [disabledCapabilities, setDisabledCapabilities]: ReactState<Nullable<DisabledCapabilities>> =
        useState<Nullable<DisabledCapabilities>>(null)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    useEffect((): void => {
        getCMEDisabledCapabilitiesConfig()
            .then((response: JSONResponse<CMEDisabledCapabilitiesConfigDTO>): void => {
                if (isMounted.current) {
                    setDisabledCapabilities(response?.disabledCapabilities ?? null)
                }
            })
            .catch((): void => {
                if (isMounted.current) {
                    setDisabledCapabilities(null)
                }
            })
    }, [])

    return disabledCapabilities
}
